import { KeyIcon } from '@phosphor-icons/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Formik } from 'components/form/formik';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import { useTranslation } from 'contexts/translation-context';
import { nullthrows } from '../../../utils/invariant';

import { Button } from '../../../components/button/Button';
import { InputField } from '../../../components/input/InputField';
import { getDisplayError } from '../../../utils/get-display-error';
import { acceptCarrierUserInvite, useCarrierNameByInviteLink } from '../models';
import { captureException } from '@sentry/react';
import { PageHeader } from '../../../components/PageHeader';
import { SpinnerBlock } from '../../../components/Spinner';

const setPasswordSchema = Yup.object().shape({
  password: Yup.string().required('Required').min(8, 'Password must be at least 8 characters'),
  repeatPassword: Yup.string()
    .required('Required')
    .oneOf([Yup.ref('password')], 'Passwords must match'),
});

export const SupplierUserInvitePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const email = nullthrows(searchParams.get('email'), 'Email not found');
  const supplierId = parseInt(nullthrows(searchParams.get('carrierId'), 'Supplier ID not found'), 10);
  const token = nullthrows(searchParams.get('token'), 'Token not found');
  const { i18n } = useTranslation();

  const { data, error, isLoading } = useCarrierNameByInviteLink(supplierId, email, token);

  if (isLoading) {
    return <SpinnerBlock message="Loading..." className="h-screen" />;
  }

  if (error) {
    throw error;
  }

  const supplierName = data?.carrierName ?? 'unknown';
  return (
    <div className="h-screen">
      <PageHeader
        title={i18n('carrierPortal.invite.title', {
          supplierName: supplierName,
        })}
      />

      <main className="bg-surface-inverse p-8 h-full">
        <div className="flex flex-col justify-center h-full items-center max-w-md mx-auto">
          <h1 className="heading-one mb-8 text-fg-inverse">
            {i18n('carrierPortal.invite.title', {
              supplierName: supplierName,
            })}
          </h1>

          <div className="text-fg-inverse mb-4">
            {i18n('carrierPortal.invite.details', {
              supplierName: supplierName,
            })}
          </div>

          <div className="w-full">
            <Formik
              initialValues={{
                password: '',
                repeatPassword: '',
              }}
              validationSchema={setPasswordSchema}
              onSubmit={async (values) => {
                try {
                  await acceptCarrierUserInvite(supplierId, email, token, values.password);

                  toast(i18n('carrierPortal.invite.accepted'));

                  navigate('/portal/login', { replace: true });
                } catch (err) {
                  captureException(err);
                  toast.error(
                    i18n('carrierPortal.invite.acceptError', {
                      error: getDisplayError(err),
                    }),
                  );
                }
              }}
            >
              {({ handleSubmit, isSubmitting }) => (
                <form onSubmit={handleSubmit}>
                  <InputField labelText={i18n('carrierPortal.invite.password')} type="password" name="password" />
                  <InputField
                    labelText={i18n('carrierPortal.invite.repeatPassword')}
                    type="password"
                    name="repeatPassword"
                  />

                  <div className="flex justify-between">
                    <Button
                      type="submit"
                      color="primary"
                      isDisabled={isSubmitting}
                      isLoading={isSubmitting}
                      iconLeft={<KeyIcon className="button-icon" />}
                    >
                      {i18n('carrierPortal.invite.acceptButtonText')}
                    </Button>
                  </div>
                </form>
              )}
            </Formik>
          </div>
        </div>
      </main>
    </div>
  );
};
